import { Station, StationIssue, StationRawData } from "@shared/models";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  PaginatedResponse,
  ActionWithPayload,
  StationFilter,
  WithCallback,
  StationIssueFilter,
  GetStationsConnectionsFilter,
  StationViewRawDataFilter,
} from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";

import api from "../api";
import {
  createStation,
  getStations,
  updateStation,
  activateDeactivateStation,
  loadStationsIssues,
  readStationIssue,
  getStationsConnections,
  getStationRawData,
} from "./actions";
import {
  ActivateDeactivateStationDto,
  ActivateDeactivateStationSuccess,
  CreateStationDto,
  CreateStationSuccess,
  StationConnectionResult,
} from "../interfaces";

function* getStationsSaga({ payload }: ActionWithPayload<StationFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Station> = yield call(api.getStations, payload);

    yield put(stopLoading());
    yield put(
      getStations.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getStations.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getStationRawDataSaga({
  payload,
}: ActionWithPayload<{ station_id: number; filter: StationViewRawDataFilter }>) {
  try {
    const { station_id, filter } = payload;
    yield put(startLoading());
    const response: StationRawData[] = yield call(api.getStationRawData, String(station_id), filter);

    yield put(stopLoading());
    yield put(getStationRawData.success(response));
  } catch (error) {
    yield put(getStationRawData.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getStationsConnectionsSaga({ payload }: ActionWithPayload<GetStationsConnectionsFilter>) {
  try {
    yield put(startLoading());
    const response: Record<string, StationConnectionResult> = yield call(api.getStationsConnections, payload);
    yield put(stopLoading());
    yield put(getStationsConnections.success(response));
  } catch (error) {
    yield put(getStationsConnections.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createStationSaga({ payload }: ActionWithPayload<WithCallback<CreateStationDto>>) {
  try {
    const { cb, ...rest } = payload;

    yield put(startLoading());
    const response: CreateStationSuccess = yield call(api.createStation, rest);
    yield put(stopLoading());
    yield put(createStation.success(response));
    cb && cb();
  } catch (error) {
    yield put(createStation.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updateStationSaga({ payload }: ActionWithPayload<WithCallback<CreateStationDto & { id: number }>>) {
  try {
    const { cb, ...rest } = payload;

    yield put(startLoading());
    const response: CreateStationSuccess = yield call(api.updateStation, rest);
    yield put(stopLoading());
    yield put(updateStation.success(response));
    cb && cb();
  } catch (error) {
    yield put(updateStation.failure(error as Error));
    yield put(stopLoading());
  }
}

function* activateDeactivateStationSaga({
  payload,
}: ActionWithPayload<WithCallback<ActivateDeactivateStationDto & { id: number }>>) {
  const { cb, cbError, id, ...rest } = payload;

  try {
    yield put(startLoading());
    const response: ActivateDeactivateStationSuccess = yield call(api.activateDeactivateStation, id, rest);
    yield put(stopLoading());
    yield put(activateDeactivateStation.success(response));
    cb?.();
  } catch (error) {
    cbError?.();
    yield put(activateDeactivateStation.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getStationsIssuesSaga({ payload }: ActionWithPayload<StationIssueFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<StationIssue> = yield call(api.loadStationIssues, payload);

    yield put(
      loadStationsIssues.success({
        ...response,
        is_clear: !payload.page,
      }),
    );

    yield put(stopLoading());
  } catch (error) {
    yield put(loadStationsIssues.failure(error as Error));
    yield put(stopLoading());
  }
}

function* readStationIssueSaga({ payload }: ActionWithPayload<WithCallback<{ id: number; comment: string }>>) {
  try {
    yield put(startLoading());
    const response: StationIssue = yield call(api.readStationIssue, String(payload.id), payload.comment);

    yield put(readStationIssue.success(response));
    payload.cb?.();

    yield put(stopLoading());
  } catch (error) {
    yield put(readStationIssue.failure(error as Error));
    yield put(stopLoading());
  }
}

function* stationSaga() {
  yield takeLatest(getStations.request, getStationsSaga);
  yield takeLatest(createStation.request, createStationSaga);
  yield takeLatest(updateStation.request, updateStationSaga);
  yield takeLatest(activateDeactivateStation.request, activateDeactivateStationSaga);
  yield takeLatest(loadStationsIssues.request, getStationsIssuesSaga);
  yield takeLatest(readStationIssue.request, readStationIssueSaga);
  yield takeLatest(getStationsConnections.request, getStationsConnectionsSaga);
  yield takeLatest(getStationRawData.request, getStationRawDataSaga);
}

export default stationSaga;
